import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { api, unwrap } from '@/api/client';
import { organizationsListQuery, plansQuery, qk } from '@/api/queries';
import type { OrganizationSummary } from '@/api/types';
import { paths } from '@/app/paths';
import { Button, DataTable, DropdownChip, initialsOf, Pill, SearchInput, Segmented, type Column } from '@/design-system';
import { now } from '@/lib/clock';
import { formatRelative, shortName } from '@/lib/format';
import { useSession } from '@/app/session';
import { useBreadcrumbs } from '@/shell/breadcrumbs';
import { useGlobalActions } from '@/shell/globalActions';
import { LoadError } from '@/shell/LoadError';
import { PageHeader } from '@/shell/PageHeader';
import { organizationsPageQuery, type OrgListParams } from './api';
import { BulkImportModal } from './BulkImportModal';
import { HEADLINE_TONE, headlineOf } from './status';
import s from './organizations.module.css';

type StatusFilter = 'all' | 'active' | 'trial' | 'archived';

/** The search filters as you type, without a request per keystroke. */
function useDebounced<T>(value: T, ms = 200): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

const totalQuery = {
  queryKey: qk.organizations({ total: true }),
  queryFn: () => unwrap(api.GET('/admin/organizations', { params: { query: { size: 1 } } })),
  select: (p: { total: number }) => p.total,
};

/** /organizations (DESIGN_SPEC §4.2): search, status filter, plan and PM chips, the table. */
export function OrganizationsPage() {
  const { t } = useTranslation('organizations');
  const { t: tc } = useTranslation('common');
  useBreadcrumbs([{ label: t('title') }]);
  const navigate = useNavigate();
  const { openNewOrganization } = useGlobalActions();
  const { can } = useSession();
  // «Импорт из файла» (P8-A06, additive): for an Admin, or a PM granted organization:bulk_create.
  const [importing, setImporting] = useState(false);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<StatusFilter>('all');
  const [plan, setPlan] = useState('');
  const [pm, setPm] = useState('');
  const q = useDebounced(search.trim());
  const params: OrgListParams = { ...(q ? { search: q } : {}), ...(status !== 'all' ? { status } : {}), ...(plan ? { plan } : {}), ...(pm ? { pm } : {}) };
  const list = useQuery(organizationsPageQuery(params));
  const total = useQuery(totalQuery).data;
  const plans = useQuery(plansQuery).data ?? [];
  const all = useQuery(organizationsListQuery).data;
  // The PM filter lists the PMs of the organizations the caller sees (a PM cannot list users).
  const pms = useMemo(() => {
    const seen = new Map<string, string>();
    for (const o of all ?? []) for (const p of o.pms) seen.set(p.id, p.full_name);
    return [...seen].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [all]);

  const rows = list.data?.items ?? [];
  const columns: Column<OrganizationSummary>[] = [
    {
      key: 'org',
      header: t('list.table.organization'),
      width: 'minmax(0,2.2fr)',
      render: (o) => (
        <div className={s.orgCell}>
          <div className={s.orgTile}>{initialsOf(o.brand_name)}</div>
          <div className={s.orgText}>
            <span className={s.orgName}>{o.brand_name}</span>
            <span className={s.orgLegal}>{o.legal_name ?? tc('dash')}</span>
          </div>
        </div>
      ),
    },
    { key: 'industry', header: t('list.table.industry'), width: 'minmax(0,1fr)', render: (o) => <span className={s.muted}>{o.industry ?? tc('dash')}</span> },
    { key: 'plan', header: t('list.table.plan'), width: 'minmax(0,.8fr)', render: (o) => <span className={s.muted}>{o.plan?.name ?? tc('dash')}</span> },
    { key: 'pm', header: t('list.table.pm'), width: 'minmax(0,1fr)', render: (o) => <span className={s.pm}>{o.pms[0] ? shortName(o.pms[0].full_name) : tc('dash')}</span> },
    { key: 'sites', header: t('list.table.sites'), width: '64px', render: (o) => <span className={s.num}>{o.sites_count ?? 0}</span> },
    {
      key: 'status',
      header: t('list.table.status'),
      width: 'minmax(0,1.1fr)',
      render: (o) => {
        const h = headlineOf(o);
        return (
          <Pill tone={HEADLINE_TONE[h]} start>
            {t(`headline.${h}`)}
          </Pill>
        );
      },
    },
    { key: 'updated', header: t('list.table.updated'), width: 'minmax(0,.8fr)', render: (o) => <span className={s.updated}>{formatRelative(o.updated_at, now(), true)}</span> },
  ];

  const planName = plans.find((p) => p.id === plan)?.name;
  const pmName = pms.find((p) => p.id === pm)?.name;
  return (
    <>
      <PageHeader
        eyebrow={list.data && total !== undefined ? t('list.eyebrow', { shown: list.data.total, total }) : ''}
        title={t('title')}
        action={
          <div className={s.headActions}>
            {can('organization:bulk_create') && (
              <Button variant="secondary" icon="file-spreadsheet" onClick={() => setImporting(true)}>
                {t('list.import')}
              </Button>
            )}
            <Button icon="plus" onClick={openNewOrganization}>
              {t('list.new')}
            </Button>
          </div>
        }
      />
      <div className={s.toolbar}>
        <SearchInput value={search} onChange={setSearch} placeholder={t('list.search')} label={t('list.search_label')} />
        <Segmented
          label={t('list.filter_label')}
          value={status}
          onChange={setStatus}
          options={(['all', 'active', 'trial', 'archived'] as const).map((k) => ({ value: k, label: t(`list.filters.${k}`) }))}
        />
        <DropdownChip
          label={t('list.plan_chip', { plan: planName ?? t('list.all') })}
          menuLabel={t('list.plan_menu')}
          value={plan}
          onChange={setPlan}
          options={[{ value: '', label: t('list.all') }, ...plans.map((p) => ({ value: p.id, label: p.name }))]}
        />
        <DropdownChip
          label={t('list.pm_chip', { pm: pmName ? shortName(pmName) : t('list.all') })}
          menuLabel={t('list.pm_menu')}
          value={pm}
          onChange={setPm}
          options={[{ value: '', label: t('list.all') }, ...pms.map((p) => ({ value: p.id, label: p.name }))]}
        />
      </div>
      {importing && <BulkImportModal onClose={() => setImporting(false)} />}
      {list.isError ? (
        <LoadError error={list.error} onRetry={() => void list.refetch()} />
      ) : (
        list.data && (
          <DataTable
            label={t('list.table.label')}
            rows={rows}
            rowKey={(o) => o.id}
            columns={columns}
            onRowClick={(o) => navigate(paths.organization(o.id, 'overview'))}
            empty={t('list.empty')}
          />
        )
      )}
    </>
  );
}
